import type { ComponentChildren } from 'preact';
import './ProposeCard.css';

export interface ProposeCardProps {
  /** The live SourceCitationChip for the capture this card proposes. */
  citation: ComponentChildren;
  /** Display-face headline — the record this would create/update. */
  title: string;
  /** The interpretation-labelled summary line (composed of
   *  InterpretationLabel + verbatim text by the caller). */
  meta: ComponentChildren;
  /** "File to **Auditions** · attach the **sides PDF** from the email" —
   *  composed by the caller so the bold filing target/attachment stays
   *  semantic markup, not a string this component has to parse. */
  fileTarget: ComponentChildren;
  /** When set, this card is an ASK — a person's held cascade awaiting the
   *  authority holder (C3's on_exceed conversion), not a system proposal.
   *  Renders the requester line and relabels Approve. Same primitive 03,
   *  new sender — deliberately NOT a new component. */
  requester?: string;
  onApprove: () => void;
  /** Optional — the "Edit" door is shown only when the caller can open the
   *  proposed record to adjust it (Today's proposals do). A capture proposal
   *  whose only sensible resolutions are Approve / Decline omits it. */
  onEdit?: () => void;
  /** Decline is the honest terminal: it permanently rejects (bc0bd4a8 —
   *  decline is a first-class outcome). Deferral deliberately has NO
   *  button — a proposal nobody acts on stays pending in the queue, so
   *  scrolling past IS "not now" (labels amended by Huan, 2026-08-07:
   *  "Correct" read as an adjective; "Not now" promised a defer the
   *  machinery never performed). */
  onDecline: () => void;
}

// Trust-posture primitive (3) — propose, don't just act. Composes the
// citation chip + interpretation labels + a filing target into one card
// whose only exit is a decision: Approve (the one gold CTA per screen),
// Edit, or Decline.
export function ProposeCard({ citation, title, meta, fileTarget, requester, onApprove, onEdit, onDecline }: ProposeCardProps) {
  return (
    <div class="vw-propose-card">
      {citation}
      {requester ? <div class="vw-propose-card__requester">asked by {requester}</div> : null}
      <div class="vw-propose-card__title">{title}</div>
      <div class="vw-propose-card__meta">{meta}</div>
      <div class="vw-propose-card__file">{fileTarget}</div>
      <div class="vw-propose-card__btns">
        <button type="button" class="vw-propose-card__btn vw-propose-card__btn--approve" onClick={onApprove}>
          Approve
        </button>
        {onEdit ? (
          <button type="button" class="vw-propose-card__btn" onClick={onEdit}>
            Edit
          </button>
        ) : null}
        <button type="button" class="vw-propose-card__btn vw-propose-card__btn--ghost" onClick={onDecline}>
          Decline
        </button>
      </div>
    </div>
  );
}
